import { Box, Typography, Chip } from '@mui/material';
import { vortxColors, vortxTypography, chartColors } from '@vortx/shared-ui';
import AnimatedMetric from '../AnimatedMetric';
import StepSlide, { SplitView, DemoBullets } from '../StepSlide';
import { retroColors, demoColors } from '../theme';
import {
    SearchOutlined,
    ReceiptOutlined,
    CalculateOutlined,
    AccountBalanceOutlined,
    BoltOutlined,
    EvStationOutlined,
    PowerOutlined,
    SyncAltOutlined,
} from '@mui/icons-material';

/**
 * Step 6: "Site DNA — Intelligence Before Capital"
 */
export function Step6_SiteDNA({ visible }) {
    const pipeline = [
        { icon: <SearchOutlined />, label: 'Screen', desc: 'Enter address → utility capacity + distribution voltage + fiber proximity + zoning' },
        { icon: <ReceiptOutlined />, label: 'Tariff', desc: 'Upload bill → Gemini extraction → rates, demand charges, interconnection costs' },
        { icon: <CalculateOutlined />, label: 'Size', desc: 'MILP optimization → BESS capacity that maximizes development yield' },
        { icon: <AccountBalanceOutlined />, label: 'Finance', desc: '25-year cash flow → RA revenue + arbitrage + DCM + degradation' },
    ];

    return (
        <StepSlide
            title="Site DNA — Intelligence Before Capital"
            subtitle="Before a dollar is committed, Site DNA answers: should we build here?"
            keyStat="Screen 50 sites, design 10, build 6. Site DNA automates the funnel that used to take an engineering team weeks per site."
            visible={visible}
        >
            {/* Pipeline cards */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
                gap: 2,
                mb: 4,
            }}>
                {pipeline.map((step, i) => (
                    <Box key={i} sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: retroColors.surface,
                        border: `1px solid rgba(178, 140, 80, 0.1)`,
                        position: 'relative',
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.6rem',
                            color: retroColors.muted,
                        }}>
                            {i + 1}/4
                        </Box>
                        <Box sx={{ color: vortxColors.gold, mb: 1 }}>{step.icon}</Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: vortxColors.white,
                            mb: 0.5,
                        }}>
                            {step.label}
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.8rem',
                            color: retroColors.dimmed,
                            lineHeight: 1.5,
                        }}>
                            {step.desc}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Stats row */}
            <Box sx={{
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
                flexWrap: 'wrap',
            }}>
                <AnimatedMetric value={10000} suffix="+" label="Utility Tariffs" />
                <AnimatedMetric value={25} suffix=" yr" label="Financial Projections" />
                <AnimatedMetric value={45} suffix="s" label="Solve Time" />
            </Box>

            <Box sx={{
                mt: 3,
                p: 2,
                borderRadius: 1,
                bgcolor: 'rgba(178, 140, 80, 0.05)',
                border: '1px solid rgba(178, 140, 80, 0.1)',
                textAlign: 'center',
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.body,
                    fontSize: '0.9rem',
                    color: retroColors.dimmed,
                }}>
                    Not guessing at battery size. MILP optimization finds the BESS capacity that maximizes development yield given the local tariff, RA market, and grid constraints. Capital partners get a bankable pro forma before breaking ground.
                </Typography>
            </Box>
        </StepSlide>
    );
}

/**
 * Step 7: "The Solver — 35,040 Optimal Decisions"
 */
export function Step7_Solver({ visible }) {
    const dispatchMoments = [
        { time: '4:00 AM', action: 'Charge from grid during off-peak TOU', price: '$0.03/kWh', color: chartColors.grid },
        { time: '2:00 PM', action: 'Discharge during RA event window', price: 'Capacity obligation', color: vortxColors.error },
        { time: '6:00 PM', action: 'Discharge during peak TOU spread', price: '$0.25/kWh', color: vortxColors.gold },
        { time: '9:00 PM', action: 'Reserve maintained for site backup', price: 'Reliability SLA', color: vortxColors.success },
    ];

    const techCallouts = [
        'Julia MILP with HiGHS — 10-100x faster than Python solvers',
        'Two-tier battery degradation: shallow vs. deep cycle costing',
        'Revenue stack optimization: which hours earn from RA vs. arbitrage vs. DCM',
        'Operating envelope: grid import/export limits, ramp rates, min SOC',
    ];

    return (
        <StepSlide
            title="The Solver — 35,040 Optimal Decisions"
            subtitle="The solver doesn't just do one thing. It co-optimizes across competing revenue streams while maintaining backup reserves."
            behindTheScenes="Julia MILP with HiGHS. 10-100x faster than Python solvers. Two-tier battery degradation (calendar + cycle fade). Revenue stack optimization across RA, arbitrage, DCM, and frequency regulation."
            keyStat="45-second solve time. Revenue-optimal dispatch for 8,760 hours. Every hour earns from the best available stream."
            visible={visible}
        >
            {/* Pipeline visualization */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 4,
                flexWrap: 'wrap',
            }}>
                {['Frontend', 'Platform API', 'Backend', 'Julia Solver', 'Results'].map((stage, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                            label={stage}
                            size="small"
                            sx={{
                                bgcolor: stage === 'Julia Solver' ? 'rgba(178, 140, 80, 0.15)' : retroColors.surface,
                                color: stage === 'Julia Solver' ? vortxColors.gold : retroColors.dimmed,
                                border: `1px solid ${stage === 'Julia Solver' ? vortxColors.gold : 'rgba(136,136,136,0.2)'}`,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                            }}
                        />
                        {i < 4 && (
                            <Typography sx={{ color: retroColors.muted, mx: 0.5, fontSize: '0.8rem' }}>→</Typography>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Dispatch moments */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
                gap: 2,
                mb: 3,
            }}>
                {dispatchMoments.map((moment, i) => (
                    <Box key={i} sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: retroColors.surface,
                        borderLeft: `3px solid ${moment.color}`,
                    }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: moment.color,
                            mb: 0.5,
                        }}>
                            {moment.time}
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.8rem',
                            color: vortxColors.white,
                            mb: 0.5,
                        }}>
                            {moment.action}
                        </Typography>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: retroColors.muted,
                        }}>
                            {moment.price}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </StepSlide>
    );
}

/**
 * Step 8: "Multi-Agent Coordination — The On-Site Controller"
 */
export function Step8_Coordination({ visible }) {
    const agents = [
        {
            name: 'BESS Agent',
            icon: <BoltOutlined />,
            message: '"I want to discharge at 2pm for RA compliance"',
            color: vortxColors.gold,
        },
        {
            name: 'EV Charging Agent',
            icon: <EvStationOutlined />,
            message: '"I need 500 kW for tenant fleet charging by 6am"',
            color: chartColors.ev,
        },
        {
            name: 'Backup Gen Agent',
            icon: <PowerOutlined />,
            message: '"I hold 200 kW reserve for site reliability SLA"',
            color: vortxColors.success,
        },
    ];

    return (
        <StepSlide
            title="Multi-Agent Coordination"
            subtitle="A powered land site isn't just a battery. It's multiple assets sharing one interconnection. Flux EMS coordinates all of it."
            behindTheScenes="ADMM decomposition. Model Predictive Control every 15 minutes. 35,040 timesteps/year. No human in the loop."
            keyStat="97% of theoretical global optimum. Autonomous from BESS commissioning."
            visible={visible}
        >
            {/* ADMM visualization */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                mt: 2,
            }}>
                {/* Agents */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                    gap: 3,
                    width: '100%',
                }}>
                    {agents.map((agent, i) => (
                        <Box key={i} sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: retroColors.surface,
                            border: `1px solid ${agent.color}33`,
                            textAlign: 'center',
                        }}>
                            <Box sx={{ color: agent.color, mb: 1 }}>{agent.icon}</Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.heading,
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                color: agent.color,
                                mb: 1,
                            }}>
                                {agent.name}
                            </Typography>
                            <Typography sx={{
                                fontFamily: vortxTypography.body,
                                fontSize: '0.8rem',
                                color: retroColors.dimmed,
                                fontStyle: 'italic',
                            }}>
                                {agent.message}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Arrows converging to coordinator */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <SyncAltOutlined sx={{ fontSize: 40, color: retroColors.muted }} />
                </Box>

                {/* Coordinator */}
                <Box sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: 'rgba(178, 140, 80, 0.08)',
                    border: `2px solid ${vortxColors.gold}`,
                    textAlign: 'center',
                    maxWidth: 500,
                    width: '100%',
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        color: vortxColors.gold,
                        mb: 1,
                    }}>
                        ADMM Coordinator
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.body,
                        fontSize: '0.85rem',
                        color: vortxColors.white,
                    }}>
                        Adjusts congestion prices until all agents agree on shared interconnection capacity.
                    </Typography>
                </Box>

                {/* Key principles */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}>
                    {[
                        'Negotiate through prices, not rules',
                        'Near-globally-optimal without central planning',
                        'New asset? Add an agent. Protocol unchanged.',
                    ].map((principle, i) => (
                        <Chip
                            key={i}
                            label={principle}
                            sx={{
                                bgcolor: retroColors.surface,
                                color: retroColors.dimmed,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                border: '1px solid rgba(178, 140, 80, 0.1)',
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </StepSlide>
    );
}
